"use client";

import { useBuilderUnsavedChanges } from "./useBuilderUnsavedChanges";

import {
  reconcileSiteBuilderRichTextSegments,
} from "@webdo/site-templates/builder";
import {
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type ChangeEvent,
  type ReactNode,
} from "react";
import {
  builderTranslationContextKey,
  builderTranslationFieldKey,
  builderTranslationValueText,
  completeBuilderTranslationDraftSave,
  createBuilderTranslationDraft,
  reconcileBuilderTranslationDraft,
} from "./translationDraft";
import type {
  BuilderTranslationField,
  BuilderTranslationFieldGroup,
  BuilderTranslationLanguage,
  BuilderTranslationPanelLabels,
  BuilderTranslationSaveChange,
  BuilderTranslationStatus,
  BuilderTranslationValue,
} from "./translationTypes";

const statusClasses: Record<BuilderTranslationStatus, string> = {
  current: "border-emerald-400/35 bg-emerald-400/10 text-success",
  default: "border-primary/35 bg-primary/10 text-primary",
  missing: "border-rose-400/35 bg-rose-400/10 text-danger",
  stale: "border-amber-400/35 bg-amber-400/10 text-warning",
};

function TranslationDisclosure({
  children,
  className,
  defaultOpen,
  forceOpen = false,
}: {
  children: ReactNode;
  className: string;
  defaultOpen: boolean;
  forceOpen?: boolean;
}) {
  const [isOpen, setIsOpen] = useState(defaultOpen);

  return (
    <details
      className={className}
      open={forceOpen || isOpen}
      onToggle={(event) => {
        if (forceOpen && !event.currentTarget.open) {
          event.currentTarget.open = true;
          return;
        }

        setIsOpen(event.currentTarget.open);
      }}
    >
      {children}
    </details>
  );
}

function translationStatus(
  fields: readonly BuilderTranslationField[],
): BuilderTranslationStatus {
  const statuses = fields.map((field) => field.status);

  if (statuses.includes("missing")) return "missing";
  if (statuses.includes("stale")) return "stale";
  if (statuses.includes("default")) return "default";

  return "current";
}

function completedFields(
  fields: readonly BuilderTranslationField[],
): number {
  return fields.filter((field) => (
    field.status === "current" || field.status === "default"
  )).length;
}

function translatedValue(
  field: BuilderTranslationField,
  text: string,
): BuilderTranslationValue {
  if (field.format === "plainText") {
    return text;
  }

  const ownSegments = (
    Array.isArray(field.value)
    && field.valueSource === "translation"
  )
    ? field.value
    : Array.isArray(field.sourceValue)
      ? field.sourceValue
      : [];

  return reconcileSiteBuilderRichTextSegments([...ownSegments], text);
}

function defaultGroups(
  fields: readonly BuilderTranslationField[],
): BuilderTranslationFieldGroup[] {
  return [{
    fields: [...fields],
    key: "general",
    label: "",
  }];
}

export type BuilderTranslationPanelProps = {
  canvasLanguageCode: string;
  disabled: boolean;
  groupFields?: (
    fields: readonly BuilderTranslationField[],
  ) => BuilderTranslationFieldGroup[];
  labels: BuilderTranslationPanelLabels;
  languages: BuilderTranslationLanguage[];
  onSave: BuilderTranslationSaveHandler;
};

export type BuilderTranslationSaveHandler = {
  (
    languageCode: string,
    changes: BuilderTranslationSaveChange[],
  ): Promise<void>;
};

export function BuilderTranslationPanel({
  canvasLanguageCode,
  disabled,
  groupFields = defaultGroups,
  labels,
  languages,
  onSave,
}: BuilderTranslationPanelProps) {
  const titleId = useId();
  const initialLanguageCode = (
    languages.find((language) => language.code === canvasLanguageCode)?.code
    ?? languages.find((language) => language.isDefault)?.code
    ?? languages[0]?.code
    ?? ""
  );
  const [activeLanguageCode, setActiveLanguageCode] = useState(
    initialLanguageCode,
  );
  const activeLanguage = (
    languages.find((language) => language.code === activeLanguageCode)
    ?? languages.find((language) => language.code === initialLanguageCode)
    ?? null
  );
  const [draftState, setDraftState] = useState(() => (
    createBuilderTranslationDraft(activeLanguage)
  ));
  const draftContextKeyRef = useRef(draftState.contextKey);
  draftContextKeyRef.current = draftState.contextKey;
  const { draft, resetFields } = draftState;
  const [saving, setSaving] = useState(false);
  const savingRef = useRef(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const languageSignature = JSON.stringify(
    languages.map((language) => ({
      code: language.code,
      fields: language.fields.map((field) => ({
        key: builderTranslationFieldKey(field),
        revision: field.expectedRevision,
        sourceRevision: field.expectedSourceRevision,
        status: field.status,
        value: field.value,
        valueSource: field.valueSource,
      })),
    })),
  );
  const dirtyFieldKeys = useMemo(() => (
    new Set((activeLanguage?.fields ?? []).flatMap((field) => {
      const key = builderTranslationFieldKey(field);

      return (
        resetFields.has(key)
        || draft[key] !== draftState.baseline[key]
      ) ? [key] : [];
    }))
  ), [
    activeLanguage,
    draft,
    draftState.baseline,
    resetFields,
  ]);
  const staleFieldKeys = useMemo(() => (
    new Set((activeLanguage?.fields ?? []).flatMap((field) => (
      field.status === "stale" && field.valueSource === "translation"
        ? [builderTranslationFieldKey(field)]
        : []
    )))
  ), [activeLanguage]);
  const hasUnsavedChanges = dirtyFieldKeys.size > 0;
  const hasSavableChanges = hasUnsavedChanges || staleFieldKeys.size > 0;
  const unsavedChanges = useBuilderUnsavedChanges({ dirty: hasUnsavedChanges || saving });

  useEffect(() => {
    const nextLanguage = (
      languages.find((language) => language.code === activeLanguageCode)
      ?? languages.find((language) => language.code === initialLanguageCode)
      ?? null
    );

    if (!nextLanguage) {
      setActiveLanguageCode("");
      setDraftState(createBuilderTranslationDraft(null));
      setSaved(false);
      setError(null);
      return;
    }

    if (nextLanguage.code !== activeLanguageCode) {
      setActiveLanguageCode(nextLanguage.code);
    }

    const contextChanged = (
      draftState.contextKey
      !== builderTranslationContextKey(nextLanguage)
    );

    setDraftState((current) => (
      reconcileBuilderTranslationDraft(current, nextLanguage)
    ));

    if (contextChanged) {
      setSaved(false);
      setError(null);
    }
  }, [
    activeLanguageCode,
    draftState.contextKey,
    initialLanguageCode,
    languageSignature,
    languages,
  ]);

  async function selectLanguage(language: BuilderTranslationLanguage): Promise<void> {
    if (language.code === activeLanguageCode || !language.isAvailable) {
      return;
    }

    if (
      hasUnsavedChanges
      && !(await unsavedChanges.confirmDiscard(labels.discardLanguageConfirm))
    ) {
      return;
    }

    setActiveLanguageCode(language.code);
    setDraftState(createBuilderTranslationDraft(language));
    setSaved(false);
    setError(null);
  }

  async function discard(): Promise<void> {
    if (!(await unsavedChanges.confirmDiscard())) return;
    setDraftState(createBuilderTranslationDraft(activeLanguage));
    setSaved(false);
    setError(null);
  }

  async function save(): Promise<void> {
    if (savingRef.current || !activeLanguage || !hasSavableChanges) {
      return;
    }

    const changes = activeLanguage.fields.flatMap(
      (field): BuilderTranslationSaveChange[] => {
        const key = builderTranslationFieldKey(field);

        if (resetFields.has(key)) {
          return [{
            blockUuid: field.blockUuid,
            expectedRevision: field.expectedRevision,
            expectedSourceRevision: field.expectedSourceRevision,
            fieldCode: field.fieldCode,
            format: field.format,
            value: null,
          }];
        }

        if (!dirtyFieldKeys.has(key) && !staleFieldKeys.has(key)) {
          return [];
        }

        return [{
          blockUuid: field.blockUuid,
          expectedRevision: field.expectedRevision,
          expectedSourceRevision: field.expectedSourceRevision,
          fieldCode: field.fieldCode,
          format: field.format,
          value: translatedValue(field, draft[key] ?? ""),
        }];
      },
    );

    if (changes.length === 0) {
      return;
    }

    const savedContextKey = draftState.contextKey;

    savingRef.current = true;
    setSaving(true);
    setError(null);
    setSaved(false);

    try {
      await onSave(activeLanguage.code, changes);
      setDraftState((current) => (
        completeBuilderTranslationDraftSave(current, savedContextKey)
      ));

      if (draftContextKeyRef.current === savedContextKey) {
        setSaved(true);
      }
    } catch (saveError) {
      setError(
        saveError instanceof Error ? saveError.message : labels.saveError,
      );
    } finally {
      savingRef.current = false;
      setSaving(false);
    }
  }

  if (!activeLanguage) {
    return (
      <section className="p-4 text-xs text-text-secondary">
        {labels.noFields}
      </section>
    );
  }

  const currentLanguage = activeLanguage;
  const groupedBlocks = activeLanguage.fields.reduce<Array<{
    blockLabel: string;
    blockUuid: string;
    fields: BuilderTranslationField[];
  }>>((groups, field) => {
    const existing = groups.find(
      (group) => group.blockUuid === field.blockUuid,
    );

    if (existing) {
      existing.fields.push(field);
    } else {
      groups.push({
        blockLabel: field.blockLabel,
        blockUuid: field.blockUuid,
        fields: [field],
      });
    }

    return groups;
  }, []);
  const firstIncompleteBlockIndex = Math.max(
    0,
    groupedBlocks.findIndex((group) => {
      const status = translationStatus(group.fields);
      return status === "missing" || status === "stale";
    }),
  );

  function renderTranslationField(field: BuilderTranslationField) {
    const key = builderTranslationFieldKey(field);
    const isReset = resetFields.has(key);
    const sourceText = builderTranslationValueText(field.sourceValue);
    const showSource = (
      field.status === "missing"
      || field.status === "stale"
      || field.valueSource === "fallback"
      || field.valueSource === "default"
    );
    const inputValue = isReset ? "" : draft[key] ?? "";
    const commonProps = {
      className: "mt-1 w-full rounded border border-border bg-surface px-2 py-1.5 text-xs text-text outline-none transition-colors placeholder:text-text-secondary focus:border-primary disabled:cursor-not-allowed disabled:opacity-50",
      disabled: disabled || saving || isReset,
      id: `builder-translation-${currentLanguage.code}-${key}`,
      onChange: (
        event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
      ) => {
        setDraftState((current) => ({
          ...current,
          draft: {
            ...current.draft,
            [key]: event.target.value,
          },
        }));
        setSaved(false);
        setError(null);
      },
      placeholder: (
        showSource && sourceText !== "" ? sourceText : labels.emptyValue
      ),
      value: inputValue,
    };

    return (
      <div key={key}>
        <div className="flex items-center justify-between gap-2">
          <label
            className="text-[11px] font-medium text-text-secondary"
            htmlFor={commonProps.id}
          >
            {field.fieldLabel}
          </label>
          <span
            className={[
              "border px-1 py-0.5 text-[9px] font-semibold",
              statusClasses[field.status],
            ].join(" ")}
          >
            {labels.status[field.status]}
          </span>
        </div>
        {(
          field.multiline
          || field.format === "richText"
          || inputValue.includes("\n")
        ) ? (
          <textarea {...commonProps} rows={3} />
        ) : (
          <input {...commonProps} type="text" />
        )}
        {field.format === "richText" ? (
          <p className="mt-1 text-[10px] text-text-secondary">
            {labels.richTextFormattingPreserved}
          </p>
        ) : null}
        {showSource && sourceText !== "" ? (
          <div className="mt-1.5 border-l-2 border-primary/35 pl-2">
            <span className="block text-[9px] font-semibold uppercase tracking-wide text-primary/80">
              {labels.sourceReference}
            </span>
            <p className="mt-0.5 whitespace-pre-wrap text-[10px] leading-4 text-text-secondary">
              {sourceText}
            </p>
          </div>
        ) : null}
        <button
          type="button"
          className="mt-1.5 text-[10px] font-semibold text-primary transition-colors hover:text-primary disabled:cursor-not-allowed disabled:opacity-45"
          disabled={(
            disabled
            || saving
            || (field.valueSource !== "translation" && !isReset)
          )}
          onClick={() => {
            setDraftState((current) => {
              const next = new Set(current.resetFields);
              next.add(key);
              return {
                ...current,
                resetFields: next,
              };
            });
            setSaved(false);
            setError(null);
          }}
        >
          {isReset ? labels.sourceWillBeUsed : labels.useSource}
        </button>
      </div>
    );
  }

  return (
    <section
      aria-labelledby={titleId}
      className="border-b border-border bg-surface px-2.5 py-3 text-text"
      data-builder-translation-panel="true"
    >
      <div>
        <h3 className="text-xs font-semibold text-text" id={titleId}>
          {labels.title}
        </h3>
        <p className="mt-1 text-[11px] leading-4 text-text-secondary">
          {labels.description(
            languages.filter((language) => (
              language.status === "current"
              || language.status === "default"
            )).length,
            languages.length,
          )}
        </p>
        <p className="mt-1 text-[11px] leading-4 text-primary/80">
          {labels.canvasLanguageNotice(
            languages.find(
              (language) => language.code === canvasLanguageCode,
            )?.name ?? canvasLanguageCode.toUpperCase(),
          )}
        </p>
      </div>

      <div
        aria-label={labels.languages}
        className="mt-3 flex flex-wrap gap-1.5"
        role="tablist"
      >
        {languages.map((language) => {
          const selected = language.code === activeLanguage.code;

          return (
            <button
              type="button"
              aria-selected={selected}
              className={[
                "min-h-8 border px-2 text-[10px] font-semibold transition-colors disabled:cursor-not-allowed disabled:opacity-45",
                selected
                  ? "border-primary bg-primary/15 text-primary"
                  : "border-border bg-surface-hover text-text-secondary hover:border-primary/70 hover:text-text",
              ].join(" ")}
              disabled={disabled || saving || !language.isAvailable}
              key={language.code}
              onClick={() => selectLanguage(language)}
              role="tab"
            >
              <span className="uppercase">{language.code}</span>
              {language.isDefault ? (
                <span className="ml-1 text-primary">•</span>
              ) : null}
            </button>
          );
        })}
      </div>

      <div className="mt-3 flex flex-wrap items-center gap-1.5">
        <span className="text-xs font-semibold text-text">
          {activeLanguage.name}
        </span>
        {activeLanguage.isDefault ? (
          <span className="border border-primary/30 bg-primary/10 px-1.5 py-0.5 text-[10px] font-semibold text-primary">
            {labels.sourceLanguage}
          </span>
        ) : null}
        <span
          className={[
            "border px-1.5 py-0.5 text-[10px] font-semibold",
            statusClasses[activeLanguage.status],
          ].join(" ")}
        >
          {labels.status[activeLanguage.status]}
        </span>
        <span className="text-[10px] text-text-secondary">
          {labels.fieldProgress(
            activeLanguage.translatedFields,
            activeLanguage.totalFields,
          )}
        </span>
      </div>

      {groupedBlocks.length === 0 ? (
        <p className="mt-4 rounded-md border border-border bg-surface-hover p-3 text-xs leading-5 text-text-secondary">
          {labels.noFields}
        </p>
      ) : (
        <div className="mt-3 space-y-2">
          {groupedBlocks.map((group, blockIndex) => {
            const blockStatus = translationStatus(group.fields);
            const blockCompleted = completedFields(group.fields);
            const blockDirtyCount = group.fields.filter((field) => (
              dirtyFieldKeys.has(builderTranslationFieldKey(field))
            )).length;
            const subgroups = groupFields(group.fields);
            const firstIncompleteSubgroupIndex = Math.max(
              0,
              subgroups.findIndex((subgroup) => {
                const status = translationStatus(subgroup.fields);

                return status === "missing" || status === "stale";
              }),
            );

            return (
              <TranslationDisclosure
                className="border border-border bg-surface-hover"
                defaultOpen={(
                  blockDirtyCount > 0
                  || blockIndex === firstIncompleteBlockIndex
                )}
                forceOpen={blockDirtyCount > 0}
                key={`${currentLanguage.code}-${group.blockUuid}`}
              >
                <summary className="cursor-pointer px-2.5 py-2 marker:text-text-secondary hover:bg-surface-hover">
                  <span className="ml-1 inline-flex max-w-[calc(100%-1rem)] flex-wrap items-center gap-1.5 align-middle">
                    <span className="text-[11px] font-semibold text-text">
                      {group.blockLabel}
                    </span>
                    <span
                      className={[
                        "border px-1 py-0.5 text-[9px] font-semibold",
                        statusClasses[blockStatus],
                      ].join(" ")}
                    >
                      {labels.status[blockStatus]}
                    </span>
                    <span className="text-[9px] text-text-secondary">
                      {labels.fieldProgress(
                        blockCompleted,
                        group.fields.length,
                      )}
                    </span>
                    {blockDirtyCount > 0 ? (
                      <span className="text-[9px] font-semibold text-warning">
                        {labels.unsavedFields(blockDirtyCount)}
                      </span>
                    ) : null}
                  </span>
                </summary>
                <div className="border-t border-border px-2.5 py-2.5">
                  {subgroups.length === 1 ? (
                    <div className="space-y-3">
                      {subgroups[0].fields.map(renderTranslationField)}
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {subgroups.map((subgroup, subgroupIndex) => {
                        const subgroupStatus = translationStatus(
                          subgroup.fields,
                        );
                        const subgroupCompleted = completedFields(
                          subgroup.fields,
                        );
                        const subgroupDirtyCount = subgroup.fields.filter(
                          (field) => dirtyFieldKeys.has(
                            builderTranslationFieldKey(field),
                          ),
                        ).length;

                        return (
                          <TranslationDisclosure
                            className="border border-border bg-surface-hover"
                            defaultOpen={(
                              subgroupDirtyCount > 0
                              || subgroupIndex
                                === firstIncompleteSubgroupIndex
                            )}
                            forceOpen={subgroupDirtyCount > 0}
                            key={`${group.blockUuid}-${subgroup.key}`}
                          >
                            <summary className="cursor-pointer px-2 py-1.5 marker:text-text-secondary hover:bg-surface-hover">
                              <span className="ml-1 inline-flex max-w-[calc(100%-1rem)] flex-wrap items-center gap-1.5 align-middle">
                                <span className="text-[10px] font-semibold text-text-secondary">
                                  {subgroup.label}
                                </span>
                                <span
                                  className={[
                                    "border px-1 py-0.5 text-[8px] font-semibold",
                                    statusClasses[subgroupStatus],
                                  ].join(" ")}
                                >
                                  {labels.status[subgroupStatus]}
                                </span>
                                <span className="text-[9px] text-text-secondary">
                                  {labels.fieldProgress(
                                    subgroupCompleted,
                                    subgroup.fields.length,
                                  )}
                                </span>
                                {subgroupDirtyCount > 0 ? (
                                  <span className="text-[9px] font-semibold text-warning">
                                    {labels.unsavedFields(subgroupDirtyCount)}
                                  </span>
                                ) : null}
                              </span>
                            </summary>
                            <div className="space-y-3 border-t border-border px-2 py-2.5">
                              {subgroup.fields.map(renderTranslationField)}
                            </div>
                          </TranslationDisclosure>
                        );
                      })}
                    </div>
                  )}
                </div>
              </TranslationDisclosure>
            );
          })}
        </div>
      )}

      {error ? (
        <p className="mt-3 text-[11px] text-danger" role="alert">
          {error}
        </p>
      ) : null}
      {saved ? (
        <p className="mt-3 text-[11px] text-success" role="status">
          {labels.saved}
        </p>
      ) : null}

      <div className="mt-3 flex justify-end gap-2 border-t border-border pt-3">
        <button
          type="button"
          className="min-h-8 border border-border bg-surface-hover px-2.5 text-[10px] font-semibold text-text transition-colors hover:border-border hover:text-text disabled:cursor-not-allowed disabled:opacity-45"
          disabled={disabled || saving || !hasUnsavedChanges}
          onClick={discard}
        >
          {labels.discard}
        </button>
        <button
          type="button"
          className="min-h-9 bg-primary px-3 text-xs font-semibold text-white transition-colors hover:bg-primary-hover disabled:cursor-not-allowed disabled:opacity-50 dark:bg-primary-active"
          disabled={disabled || saving || !hasSavableChanges}
          onClick={() => void save()}
        >
          {saving ? labels.saving : labels.save}
        </button>
      </div>
      {unsavedChanges.confirmationDialog}
    </section>
  );
}
